import { useCheckPermission } from '@/hooks/useCheckPermission'
import { motion, AnimatePresence, Variants } from 'framer-motion'
import {
  User,
  Loader2,
  Key,
  CheckCircle2,
  Mail,
  Shield,
  Calendar,
  Lock,
  Eye,
  EyeOff,
  Palette,
} from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { setTheme, ThemeType } from '@/redux/slices/themeSlice'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Formik, Form, Field, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import axios, { AxiosError } from 'axios'
import Cookies from 'js-cookie'
import toast from 'react-hot-toast'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { BASE_URL } from '@/constant'

const Profile = () => {
  const { isLoading: isProfileLoading, user: userData } = useCheckPermission()
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const dispatch = useDispatch()
  const currentTheme = useSelector((state: RootState) => state.theme.theme)

  const passwordRegex = /^(?=.*[A-Z])(?=.*[!@#$%^&*(),.?":{}|<>]).+$/

  interface ChangePasswordValues {
    password: string
    newPassword: string
    confirmPassword: string
  }

  const validationSchema = Yup.object().shape({
    password: Yup.string().required('Current password is required'),
    newPassword: Yup.string()
      .required('New password is required')
      .min(8, 'Password must be at least 8 characters')
      .matches(
        passwordRegex,
        'Password must contain at least one uppercase letter and one special character'
      ),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], 'Passwords must match')
      .required('Confirm password is required'),
  })

  const handleChangePassword = async (
    values: ChangePasswordValues,
    { setSubmitting, resetForm }: FormikHelpers<ChangePasswordValues>
  ) => {
    try {
      const token = Cookies.get('token')
      const response = await axios.post(
        `${BASE_URL}/auth/change-password`,
        {
          password: values.password,
          newPassword: values.newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (response.data.success) {
        toast.success('Password updated successfully')
        setIsChangePasswordOpen(false)
        resetForm()
      } else {
        toast.error(response.data.message || 'Failed to update password')
      }
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>
      toast.error(
        error.response?.data?.message ??
          'An error occurred during password update'
      )
    } finally {
      setSubmitting(false)
    }
  }

  const getInitials = (name: string) => {
    if (!name) return 'U'
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) {
      const firstChar = parts[0]?.[0] ?? ''
      const lastChar = parts[parts.length - 1]?.[0] ?? ''
      return (firstChar + lastChar).toUpperCase()
    }
    return parts[0]?.slice(0, 1).toUpperCase() ?? 'U'
  }

  if (isProfileLoading) {
    return (
      <div className='flex h-screen items-center justify-center bg-background'>
        <div className='relative flex flex-col items-center gap-4'>
          <div className='h-16 w-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin' />
          <p className='text-sm font-medium text-muted-foreground animate-pulse'>
            Loading profile...
          </p>
        </div>
      </div>
    )
  }

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', damping: 25, stiffness: 400 },
    },
  }

  return (
    <div className='h-full flex flex-col bg-background text-foreground selection:bg-primary/30 overflow-hidden'>
      {/* Background blobs for depth - theme aware opacity */}
      <div className='fixed inset-0 overflow-hidden pointer-events-none'>
        <div className='absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-primary/10 dark:bg-primary/5 rounded-full blur-[120px]' />
        <div className='absolute top-[20%] -right-[10%] w-[30%] h-[30%] bg-blue-500/10 dark:bg-blue-500/5 rounded-full blur-[100px]' />
      </div>

      <div className='flex-1 overflow-auto container mx-auto max-w-6xl px-6 py-12 relative z-10 custom-scrollbar'>
        <motion.div
          variants={containerVariants}
          initial='hidden'
          animate='visible'
          className='space-y-10'
        >
          {/* Header Section */}
          <div className='flex flex-col md:flex-row md:items-end justify-between gap-8 pb-10 border-b border-border'>
            <div className='flex flex-col md:flex-row items-center md:items-end gap-6'>
              <div className='relative group'>
                <div className='w-32 h-32 rounded-3xl bg-card border border-border flex items-center justify-center text-4xl font-bold text-card-foreground shadow-2xl relative overflow-hidden'>
                  <div className='absolute inset-0 bg-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500' />
                  {getInitials(userData?.name || '')}
                </div>
              </div>
              <div className='text-center md:text-left space-y-2'>
                <div className='flex items-center justify-center md:justify-start gap-3'>
                  <h1 className='text-3xl md:text4xl font-bold tracking-tight text-foreground capitalize'>
                    {userData?.name || 'User Profile'}
                  </h1>
                  <div className='px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 text-[10px] font-black uppercase tracking-tighter text-primary'>
                    PRO
                  </div>
                </div>
                <p className='text-muted-foreground font-medium text-lg flex items-center justify-center md:justify-start gap-2'>
                  <Mail size={18} className='text-primary/60' />
                  {userData?.email}
                </p>
              </div>
            </div>
          </div>

          <div className='grid grid-cols-1 lg:grid-cols-12 gap-8'>
            {/* Main Content Area */}
            <div className='lg:col-span-8 space-y-8'>
              <motion.div variants={itemVariants}>
                <Card className='border-none bg-card/50 backdrop-blur-xl rounded-[2.5rem] overflow-hidden ring-1 ring-border shadow-2xl'>
                  <div className='px-10 py-8 border-b border-border flex items-center justify-between'>
                    <div className='flex items-center gap-4'>
                      <div className='p-3 rounded-2xl bg-primary/10 text-primary'>
                        <User size={20} />
                      </div>
                      <div>
                        <h3 className='text-lg font-bold text-foreground'>
                          General Information
                        </h3>
                        <p className='text-xs text-muted-foreground'>
                          Your personal details and account settings
                        </p>
                      </div>
                    </div>
                  </div>
                  <CardContent className='p-10'>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-12'>
                      <div className='space-y-2 group'>
                        <label className='text-xs font-bold uppercase tracking-widest text-muted-foreground/60 transition-colors group-hover:text-primary/60'>
                          Full Name
                        </label>
                        <div className='text-lg truncate  font-semibold text-foreground/90 capitalize bg-accent/30 border border-border rounded-2xl p-4 transition-all hover:bg-accent/50'>
                          {userData?.name || 'Not provided'}
                        </div>
                      </div>
                      <div className='space-y-2 group'>
                        <label className='text-xs font-bold uppercase tracking-widest text-muted-foreground/60 transition-colors group-hover:text-primary/60'>
                          Email Address
                        </label>
                        <div className='flex items-center justify-between text-lg truncate font-semibold text-foreground/90 bg-accent/30 border border-border rounded-2xl p-4 transition-all hover:bg-accent/50'>
                          <span>{userData?.email || 'Not provided'}</span>
                          <CheckCircle2 className='w-5 h-5 text-primary' />
                        </div>
                      </div>
                      <div className='space-y-2 group'>
                        <label className='text-xs font-bold uppercase tracking-widest text-muted-foreground/60 transition-colors group-hover:text-primary/60'>
                          Role & Permissions
                        </label>
                        <div className='flex truncate items-center gap-3 text-lg font-semibold text-foreground/90 bg-accent/30 border border-border rounded-2xl p-4 transition-all hover:bg-accent/50'>
                          <Shield size={20} className='text-primary' />
                          <span className='capitalize'>
                            {userData?.role?.name || 'Guest'}
                          </span>
                        </div>
                      </div>
                      <div className='space-y-2 group'>
                        <label className='text-xs font-bold uppercase tracking-widest text-muted-foreground/60 transition-colors group-hover:text-primary/60'>
                          Member Since
                        </label>
                        <div className='flex items-center gap-3 text-lg font-semibold text-foreground/90 bg-accent/30 border border-border rounded-2xl p-4 transition-all hover:bg-accent/50'>
                          <Calendar size={20} className='text-primary' />
                          <span className='truncate'>
                            {userData?.created_at
                              ? new Date(
                                  userData.created_at
                                ).toLocaleDateString('en-US', {
                                  month: 'long',
                                  year: 'numeric',
                                })
                              : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Sidebar / Actions */}
            <div className='lg:col-span-4 space-y-8'>
              <motion.div variants={itemVariants}>
                <Card className='border-none bg-primary/10 backdrop-blur-xl rounded-[2.5rem] p-1 ring-1 ring-primary/20'>
                  <CardContent className='p-8 space-y-6'>
                    <div className='flex items-center gap-4 mb-2'>
                      <div className='p-3 bg-primary text-primary-foreground rounded-2xl shadow-lg shadow-primary/20'>
                        <Key size={20} />
                      </div>
                      <div>
                        <h4 className='text-lg font-bold text-foreground'>
                          Security
                        </h4>
                        <p className='text-xs text-primary/60 font-medium uppercase'>
                          Access Control
                        </p>
                      </div>
                    </div>
                    <p className='text-sm text-muted-foreground leading-relaxed'>
                      Keep your account secure by regularly updating your
                      password and monitoring access.
                    </p>
                    <Button
                      onClick={() => setIsChangePasswordOpen(true)}
                      className='w-full h-14 rounded-2xl bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-[1.02] transition-all font-black text-base shadow-xl shadow-primary/10'
                    >
                      Change Password
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Card className='border-none bg-card/50 backdrop-blur-xl rounded-[2.5rem] overflow-hidden ring-1 ring-border'>
                  <CardContent className='p-8 space-y-6'>
                    <div className='flex items-center gap-4'>
                      <div className='p-3 rounded-2xl bg-primary/10 text-primary'>
                        <Palette size={20} />
                      </div>
                      <div>
                        <h4 className='text-lg font-bold text-foreground'>
                          Interface Theme
                        </h4>
                        <p className='text-xs text-muted-foreground'>
                          Choose your preferred accent color
                        </p>
                      </div>
                    </div>

                    <div className='grid grid-cols-2 gap-3'>
                      {[
                        {
                          id: 'default',
                          name: 'Golden',
                          color: 'bg-[#ffc107]',
                        },
                        { id: 'blue', name: 'Ocean', color: 'bg-[#2196f3]' },
                        { id: 'purple', name: 'Sunset', color: 'bg-[#9c27b0]' },
                        { id: 'green', name: 'Emerald', color: 'bg-[#4caf50]' },
                      ].map(t => (
                        <button
                          key={t.id}
                          onClick={() => dispatch(setTheme(t.id as ThemeType))}
                          className={cn(
                            'flex items-center gap-3 p-3 rounded-2xl border transition-all duration-300 group',
                            currentTheme === t.id
                              ? 'bg-primary/10 border-primary ring-1 ring-primary'
                              : 'bg-accent/20 border-transparent hover:border-border'
                          )}
                        >
                          <div
                            className={cn(
                              'w-5 h-5 rounded-full shadow-inner transition-transform group-hover:scale-110',
                              t.color
                            )}
                          />
                          <span className='text-sm font-bold'>{t.name}</span>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Card className='border-none bg-card/50 backdrop-blur-xl rounded-[2.5rem] overflow-hidden ring-1 ring-border'>
                  <CardContent className='p-8 space-y-6'>
                    <h4 className='text-sm font-bold uppercase tracking-widest text-muted-foreground/60'>
                      Account Status
                    </h4>
                    <div className='space-y-4'>
                      <div className='flex items-center justify-between p-4 rounded-2xl bg-accent/30 border border-border'>
                        <span className='text-sm text-muted-foreground'>
                          Email Verified
                        </span>
                        <CheckCircle2 size={16} className='text-green-500' />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Change Password Dialog */}
        <AnimatePresence>
          <Dialog
            open={isChangePasswordOpen}
            onOpenChange={setIsChangePasswordOpen}
          >
            <DialogContent className='max-w-[500px] p-0 bg-background/95 backdrop-blur-3xl border-border shadow-[0_32px_64px_-12px_rgba(0,0,0,0.2)] dark:shadow-[0_32px_64px_-12px_rgba(0,0,0,0.6)] rounded-[40px] overflow-hidden'>
              <DialogHeader className='p-10 pb-4'>
                <div className='flex items-center gap-5'>
                  <div className='w-14 h-14 rounded-[22px] bg-primary/10 text-primary flex items-center justify-center shadow-inner'>
                    <Shield size={28} />
                  </div>
                  <div>
                    <DialogTitle className='text-3xl font-bold tracking-tight text-foreground'>
                      Security Update
                    </DialogTitle>
                    <DialogDescription className='text-muted-foreground font-medium'>
                      Change your password to keep your account safe.
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <Formik
                initialValues={{
                  password: '',
                  newPassword: '',
                  confirmPassword: '',
                }}
                validationSchema={validationSchema}
                onSubmit={handleChangePassword}
              >
                {({ isSubmitting, errors, touched, values }) => (
                  <Form className='p-10 pt-4 space-y-8'>
                    <div className='space-y-6'>
                      {/* Current Password */}
                      <div className='space-y-2.5'>
                        <Label className='text-[10px] font-black text-muted-foreground/50 uppercase tracking-[0.15em] ml-1'>
                          Current Password
                        </Label>
                        <div className='relative group'>
                          <div className='absolute left-5 top-1/2 -translate-y-1/2 text-muted-foreground/40 group-focus-within:text-primary transition-colors'>
                            <Lock size={18} />
                          </div>
                          <Field
                            as={Input}
                            name='password'
                            type={showPassword ? 'text' : 'password'}
                            placeholder='Enter current password'
                            className={cn(
                              'h-14 rounded-2xl border-border bg-accent/20 text-foreground focus:bg-accent/40 focus:border-primary/40 focus:ring-4 focus:ring-primary/5 transition-all pl-12 pr-12 text-base font-medium',
                              errors.password &&
                                touched.password &&
                                'border-red-500/50 bg-red-500/5'
                            )}
                          />
                          <button
                            type='button'
                            onClick={() => setShowPassword(!showPassword)}
                            className='absolute right-5 top-1/2 -translate-y-1/2 text-muted-foreground/40 hover:text-foreground transition-colors'
                          >
                            {showPassword ? (
                              <Eye size={18} />
                            ) : (
                              <EyeOff size={18} />
                            )}
                          </button>
                        </div>
                        {errors.password && touched.password && (
                          <motion.p
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            className='text-[11px] font-bold text-red-500/80 ml-1'
                          >
                            {errors.password}
                          </motion.p>
                        )}
                      </div>

                      {/* New Password */}
                      <div className='space-y-2.5'>
                        <Label className='text-[10px] font-black text-muted-foreground/50 uppercase tracking-[0.15em] ml-1'>
                          New Password
                        </Label>
                        <div className='relative group'>
                          <div className='absolute left-5 top-1/2 -translate-y-1/2 text-muted-foreground/40 group-focus-within:text-primary transition-colors'>
                            <Key size={18} />
                          </div>
                          <Field
                            as={Input}
                            name='newPassword'
                            type={showNewPassword ? 'text' : 'password'}
                            placeholder='Enter new password'
                            className={cn(
                              'h-14 rounded-2xl border-border bg-accent/20 text-foreground focus:bg-accent/40 focus:border-primary/40 focus:ring-4 focus:ring-primary/5 transition-all pl-12 pr-12 text-base font-medium',
                              errors.newPassword &&
                                touched.newPassword &&
                                'border-red-500/50 bg-red-500/5'
                            )}
                          />
                          <button
                            type='button'
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className='absolute right-5 top-1/2 -translate-y-1/2 text-muted-foreground/40 hover:text-foreground transition-colors'
                          >
                            {showNewPassword ? (
                              <EyeOff size={18} />
                            ) : (
                              <Eye size={18} />
                            )}
                          </button>
                        </div>

                        {/* Real-time Password Policy checklist */}
                        <div className='px-1 py-1 space-y-2'>
                          <div className='flex flex-wrap gap-x-4 gap-y-1'>
                            {[
                              {
                                label: '8+ Characters',
                                met: values.newPassword.length >= 8,
                              },
                              {
                                label: 'Uppercase',
                                met: /[A-Z]/.test(values.newPassword),
                              },
                              {
                                label: 'Special Symbol',
                                met: /[!@#$%^&*(),.?":{}|<>]/.test(
                                  values.newPassword
                                ),
                              },
                            ].map((rule, idx) => (
                              <div
                                key={idx}
                                className='flex items-center gap-1.5'
                              >
                                <div
                                  className={cn(
                                    'w-3.5 h-3.5 rounded-full flex items-center justify-center transition-all duration-500',
                                    rule.met
                                      ? 'bg-green-500/20 text-green-500'
                                      : 'bg-muted text-muted-foreground/40'
                                  )}
                                >
                                  <CheckCircle2
                                    size={10}
                                    className={
                                      rule.met ? 'opacity-100' : 'opacity-20'
                                    }
                                  />
                                </div>
                                <span
                                  className={cn(
                                    'text-[10px] font-bold transition-colors duration-500',
                                    rule.met
                                      ? 'text-foreground/80'
                                      : 'text-muted-foreground/40'
                                  )}
                                >
                                  {rule.label}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Confirm Password */}
                      <div className='space-y-2.5'>
                        <Label className='text-[10px] font-black text-muted-foreground/50 uppercase tracking-[0.15em] ml-1'>
                          Confirm Password
                        </Label>
                        <div className='relative group'>
                          <div className='absolute left-5 top-1/2 -translate-y-1/2 text-muted-foreground/40 group-focus-within:text-primary transition-colors'>
                            <CheckCircle2 size={18} />
                          </div>
                          <Field
                            as={Input}
                            name='confirmPassword'
                            type='password'
                            placeholder='Confirm your new password'
                            className={cn(
                              'h-14 rounded-2xl border-border bg-accent/20 text-foreground focus:bg-accent/40 focus:border-primary/40 focus:ring-4 focus:ring-primary/5 transition-all pl-12 text-base font-medium',
                              errors.confirmPassword &&
                                touched.confirmPassword &&
                                'border-red-500/50 bg-red-500/5'
                            )}
                          />
                        </div>
                        {errors.confirmPassword && touched.confirmPassword && (
                          <motion.p
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            className='text-[11px] font-bold text-red-500/80 ml-1'
                          >
                            {errors.confirmPassword}
                          </motion.p>
                        )}
                      </div>
                    </div>

                    <div className='flex gap-4 pt-4'>
                      <Button
                        type='button'
                        variant='ghost'
                        className='flex-1 h-14 rounded-[22px] font-bold text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-all active:scale-95'
                        onClick={() => setIsChangePasswordOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type='submit'
                        disabled={
                          isSubmitting ||
                          !values.newPassword ||
                          !!errors.newPassword
                        }
                        className='flex-[1.5] h-14 rounded-[22px] font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_20px_40px_-10px_rgba(var(--primary),0.3)] transition-all active:scale-[0.98] disabled:opacity-50 disabled:grayscale'
                      >
                        {isSubmitting ? (
                          <div className='flex items-center gap-2'>
                            <Loader2 className='w-5 h-5 animate-spin' />
                            <span>Updating...</span>
                          </div>
                        ) : (
                          'Update Password'
                        )}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </DialogContent>
          </Dialog>
        </AnimatePresence>
      </div>
    </div>
  )
}

export default Profile
